import { css } from 'aphrodite';
import { saveAs } from 'file-saver';
import * as React from 'react';
import { DownloadIconV2 } from '../../components/svgs/icons/DownloadIconV2';
import { MinusIcon } from '../../components/svgs/icons/MinusIcon';
import { PlusIcon2 } from '../../components/svgs/icons/PlusIcon';
import { XIconNew } from '../../components/svgs/icons/XIcon';
import { Button } from '../Button';
import { Dialog, DialogBody, DialogClose, DialogHeader, DialogPopup, DialogTitle } from '../Dialog';
import { styles } from './styles';

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.25;

export interface ImagePreviewDialogProps {
	alt: string;
	fileName?: string;
	open: boolean;
	src: string;
	title?: string;
	onOpenChange: (open: boolean) => void;
}

export const ImagePreviewDialog: React.FC<ImagePreviewDialogProps> = ({
	alt,
	fileName,
	open,
	src,
	title,
	onOpenChange,
}) => {
	const [zoom, setZoom] = React.useState(1);
	const resolvedFileName = fileName || getImageFileName(src);

	React.useEffect(() => {
		setZoom(1);
	}, [open, src]);

	const changeZoom = (amount: number) => {
		setZoom(current => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, current + amount)));
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange} dismissOnOutsideClick>
			<DialogPopup styles={[styles.popup]} backdropStyles={[styles.backdrop]} showCloseButton={false}>
				<DialogHeader styles={[styles.toolbar]}>
					<div className={css(styles.toolbarIdentity)}>
						<DialogClose
							render={
								<Button
									type='button'
									variant='ghost'
									size='icon'
									styles={[styles.toolbarButton]}
									aria-label='Close image preview'
									title='Close image preview'
								/>
							}
						>
							<XIconNew />
						</DialogClose>
						<DialogTitle styles={[styles.title]}>{title || resolvedFileName}</DialogTitle>
					</div>
					<Button
						type='button'
						variant='ghost'
						size='icon'
						styles={[styles.toolbarButton]}
						onClick={() => saveAs(src, resolvedFileName)}
						aria-label='Download image'
						title='Download image'
					>
						<DownloadIconV2 />
					</Button>
				</DialogHeader>
				<DialogBody
					styles={[styles.stage, ...(zoom > 1 ? [styles.stageZoomed] : [])]}
					onClick={event => {
						if (event.target === event.currentTarget) onOpenChange(false);
					}}
				>
					<img
						className={css(styles.image, zoom > 1 && styles.imageZoomed)}
						src={src}
						alt={alt}
						draggable={false}
						style={{ transform: `scale(${zoom})` }}
					/>
					<div className={css(styles.zoomControls)}>
						<Button
							type='button'
							variant='ghost'
							size='icon'
							styles={[styles.zoomButton]}
							onClick={() => changeZoom(-ZOOM_STEP)}
							disabled={zoom <= MIN_ZOOM}
							aria-label='Zoom out'
							title='Zoom out'
						>
							<MinusIcon />
						</Button>
						<span className={css(styles.zoomLevel)} aria-live='polite'>
							{Math.round(zoom * 100)}%
						</span>
						<Button
							type='button'
							variant='ghost'
							size='icon'
							styles={[styles.zoomButton]}
							onClick={() => changeZoom(ZOOM_STEP)}
							disabled={zoom >= MAX_ZOOM}
							aria-label='Zoom in'
							title='Zoom in'
						>
							<PlusIcon2 />
						</Button>
					</div>
				</DialogBody>
			</DialogPopup>
		</Dialog>
	);
};

function getImageFileName(src: string): string {
	try {
		const fileName = new URL(src, window.location.href).pathname.split('/').filter(Boolean).pop();
		return fileName ? decodeURIComponent(fileName) : 'image';
	} catch {
		return 'image';
	}
}
